import { Reaction } from '@adv-frontend/reactions';
import { entries, values } from '@direct-frontend/stdlib';
import { $canSetWhileSubmitting, BaseField, BaseFields } from '../fields/types';
import { walkField } from '../fields/utils/walkFields';
import { getFormStoreSingleton } from '../store/store';
import { BaseValidationError } from '../types';

import { $internals, FormActionsMap, FormInstance } from './types';
import { getFieldInstance } from '../fields/getFieldInstance';
import { assertFormInitialized } from './utils/assertFormInitialized';

export type StartStopReactionsOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
> = {
    /** Инстанс формы */
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
};

/**
 * Запускает все реакции формы и полей
 */
export function startAllFormReactions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
}: StartStopReactionsOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue
>) {
    const { setStartedReactions, setAreReactionsStarted, getIsUninitializing } = getFormStoreSingleton(form.name);

    if (getIsUninitializing({ formInitializationId: form[$internals].formInitializationId })) {
        return;
    }

    const startedReactions = form[$internals].reactions?.map(reaction => reaction.start(form));

    for (const [fieldName, field] of entries(form[$internals].fields)) {
        startFieldReactions({
            form: form,
            field,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Названием полей могут быть только строки
            fieldId: fieldName as string,
        });
    }

    if (startedReactions) {
        setStartedReactions({
            form: startedReactions,
            formInitializationId: form[$internals].formInitializationId,
        });
    }

    setAreReactionsStarted({ formInitializationId: form[$internals].formInitializationId, value: true });
}

/**
 * Выключает все реакции формы и полей
 *
 * @returns Список промисов всех реакций выполняющихся в данный момент
 */
export function stopAllFormReactions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
}: StartStopReactionsOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue
>) {
    const { getStartedReactions, setAreReactionsStarted, setStartedReactions, getIsUninitializing } =
        getFormStoreSingleton(form.name);

    const startedReactions = getStartedReactions({
        formInitializationId: form[$internals].formInitializationId,
    });

    const reactionsPromises: Promise<void>[] = [];

    // Останавливаем реакции формы
    startedReactions?.form?.forEach(detach => {
        const reactionInstance = detach.getInstance();
        const executionPromises = reactionInstance?.getAllExecutionPromises() ?? [];

        // Добавляем промисы запущенных реакций в общий список
        reactionsPromises.push(...executionPromises);

        if (!getIsUninitializing({ formInitializationId: form[$internals].formInitializationId })) {
            detach();
        }
    });

    // И также останавливаем реакции всех полей
    for (const fieldStaredReactions of values(startedReactions?.fields ?? {})) {
        fieldStaredReactions.forEach(detach => {
            const reactionInstance = detach.getInstance();
            const executionPromises = reactionInstance?.getAllExecutionPromises() ?? [];

            // Добавляем промисы запущенных реакций в общий список
            reactionsPromises.push(...executionPromises);
            if (!getIsUninitializing({ formInitializationId: form[$internals].formInitializationId })) {
                detach();
            }
        });
    }

    setStartedReactions({ formInitializationId: form[$internals].formInitializationId, form: [], fields: {} });
    setAreReactionsStarted({ formInitializationId: form[$internals].formInitializationId, value: false });

    return reactionsPromises;
}

/**
 * Дожидается завершения всех выполняющихся реакций формы и полей
 */
export function waitForAllRunningReactions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    assertFormInitialized({
        formName: form.name,
        formId: form.id,
        formInitializationId: form[$internals].formInitializationId,
    });

    const { getStartedReactions } = getFormStoreSingleton(form.name);
    const startedReactions = getStartedReactions({
        formInitializationId: form[$internals].formInitializationId,
    });

    const reactionsPromises: Promise<void>[] = [];

    startedReactions?.form?.forEach(detach => {
        const reactionInstance = detach.getInstance();
        const executionPromises = reactionInstance?.getAllExecutionPromises() ?? [];

        reactionsPromises.push(...executionPromises);
    });

    for (const fieldStartedReactions of values(startedReactions?.fields ?? {})) {
        fieldStartedReactions.forEach(detach => {
            const reactionInstance = detach.getInstance();
            const executionPromises = reactionInstance?.getAllExecutionPromises() ?? [];

            reactionsPromises.push(...executionPromises);
        });
    }

    return reactionsPromises.length > 0 ? Promise.allSettled(reactionsPromises) : Promise.resolve([]);
}

/**
 * Запуск реакций поля и его дочерних полей
 */
export function startFieldReactions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldId,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BaseField;
    fieldId: string;
}) {
    const { getFormValue, getFieldsState, setStartedReactions, getIsUninitializing } = getFormStoreSingleton(form.name);

    if (getIsUninitializing({ formInitializationId: form[$internals].formInitializationId })) {
        return;
    }

    const fieldsState = getFieldsState({ formInitializationId: form[$internals].formInitializationId }) ?? {};
    const formValue = getFormValue({ formInitializationId: form[$internals].formInitializationId }) ?? {};

    const fieldsReactions: Record<string, ReturnType<Reaction<void, unknown>['start']>[]> = {};

    walkField({
        field,
        fieldsState,
        formValue,
        fieldId,
        callback: (childField, childFieldId) => {
            if ('reactions' in childField) {
                const fieldId = childFieldId;

                const fieldInstance = getFieldInstance({
                    form,
                    field: childField,
                    fieldName: fieldId,
                    fieldId,
                    creationOptions: { setterOptions: { [$canSetWhileSubmitting]: true } },
                });

                for (const reaction of childField.reactions ?? []) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
                    // @ts-expect-error -- walkField работает с базовыми типами полей
                    const detach = reaction.start(fieldInstance);

                    fieldsReactions[fieldId] ??= [];
                    fieldsReactions[fieldId]?.push(detach);
                }
            }
        },
    });

    setStartedReactions({ formInitializationId: form[$internals].formInitializationId, fields: fieldsReactions });
}

/**
 * Остановка всех запущенных реакций поля и его дочерних полей
 */
export function stopFieldReactions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldId,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BaseField;
    fieldId: string;
}) {
    const { getFormValue, getFieldsState, getStartedReactions, setStartedReactions, getIsUninitializing } =
        getFormStoreSingleton(form.name);

    const fieldsState = getFieldsState({ formInitializationId: form[$internals].formInitializationId }) ?? {};
    const formValue = getFormValue({ formInitializationId: form[$internals].formInitializationId }) ?? {};

    // Копируем объект, чтобы была возможность перезаписывать значение ключей
    const fieldsReactions = {
        ...(getStartedReactions({ formInitializationId: form[$internals].formInitializationId }) ?? {}).fields,
    };

    walkField({
        field,
        fieldsState,
        formValue,
        fieldId,
        callback: (_, childFieldId) => {
            const fieldId = childFieldId;
            const fieldStartedReactions = fieldsReactions[fieldId] ?? [];

            if (fieldStartedReactions.length) {
                for (const detach of fieldStartedReactions) {
                    if (!getIsUninitializing({ formInitializationId: form[$internals].formInitializationId })) {
                        detach();
                    }
                    fieldsReactions[fieldId] = [];
                }
            }
        },
    });

    setStartedReactions({ formInitializationId: form[$internals].formInitializationId, fields: fieldsReactions });
}
